'use client'

import { forwardRef, useState, useCallback, useMemo, useEffect } from 'react'
import { Check, CreditCard, DollarSign, Calendar, Users, Shield } from 'lucide-react'
import { safeAccess } from '../utils/security'

/**
 * BIRHAUS Radio Group Component
 * 
 * Enhanced radio group implementing all 10 BIRHAUS principles:
 * - Spanish-first labels and descriptions
 * - Progressive disclosure with visual grouping
 * - Cognitive load reduction (max 5 options per group)
 * - Accessibility compliance (WCAG AA+)
 * - Visual feedback with icons and descriptions
 * - IPU Admin specific presets for forms
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export interface RadioOption {
  id: string
  value: string
  labelEs: string
  labelEn: string
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  disabled?: boolean
  icon?: React.ReactNode
  badge?: string
  badgeEs?: string
  badgeEn?: string
  recommended?: boolean
}

export interface BirhausRadioGroupProps {
  // BIRHAUS Principle #7: Bilingual by design
  label?: string
  labelEs?: string
  labelEn?: string
  
  // BIRHAUS Principle #4: Progressive disclosure
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  
  // Options and values
  options: RadioOption[]
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
  name: string // Required for radio groups
  
  // BIRHAUS Principle #2: Honest data
  error?: string
  errorEs?: string
  errorEn?: string
  
  // BIRHAUS Principle #6: Accessibility = dignity
  disabled?: boolean
  required?: boolean
  
  // Appearance
  variant?: 'default' | 'cards' | 'minimal' | 'buttons'
  size?: 'sm' | 'md' | 'lg'
  layout?: 'vertical' | 'horizontal' | 'grid'
  
  // Visual enhancements
  showIcons?: boolean
  showDescriptions?: boolean
  showBadges?: boolean
  highlightRecommended?: boolean
  
  // Validation
  maxOptions?: number // Default 5 (cognitive load reduction)
  
  // Locale
  locale?: 'es' | 'en'
  
  // IPU Admin specific
  presetType?: 'payment_methods' | 'member_status' | 'event_types' | 'donation_types' | 'custom'
}

export const BirhausRadioGroup = forwardRef<HTMLDivElement, BirhausRadioGroupProps>(({
  // Labels (Spanish-first)
  label,
  labelEs,
  labelEn,
  
  // Description
  description,
  descriptionEs,
  descriptionEn,
  
  // Options
  options,
  value,
  defaultValue,
  onChange,
  name,
  
  // Validation
  error,
  errorEs,
  errorEn,
  
  // Basic props
  disabled = false,
  required = false,
  
  // Appearance
  variant = 'default',
  size = 'md',
  layout = 'vertical',
  
  // Visual enhancements
  showIcons = true,
  showDescriptions = true,
  showBadges = true,
  highlightRecommended = true,
  
  // Validation
  maxOptions = 5,
  
  // Locale
  locale = 'es',
  
  // IPU Admin
  presetType = 'custom'
}, ref) => {
  // State management
  const [internalValue, setInternalValue] = useState<string>(value || defaultValue || '')
  const [validationError, setValidationError] = useState<string>('')
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const getDisplayText = useCallback((
    primary: string | undefined,
    spanish: string | undefined,
    english: string | undefined,
    fallback: string
  ): string => {
    if (locale === 'es') {
      return primary || spanish || english || fallback
    }
    return primary || english || spanish || fallback
  }, [locale])
  
  const displayLabel = getDisplayText(label, labelEs, labelEn, '')
  const displayDescription = getDisplayText(description, descriptionEs, descriptionEn, '')
  const displayError = error || errorEs || validationError
  
  // Get default options for IPU Admin
  const getDefaultOptions = useCallback((): RadioOption[] => {
    const optionConfigs: Record<string, RadioOption[]> = {
      payment_methods: [
        {
          id: 'cash',
          value: 'cash',
          labelEs: 'Efectivo',
          labelEn: 'Cash',
          descriptionEs: 'Pago en efectivo en guaraníes',
          descriptionEn: 'Cash payment in guaraníes',
          icon: <DollarSign className="h-5 w-5" />,
          recommended: true
        },
        {
          id: 'bank_transfer',
          value: 'bank_transfer',
          labelEs: 'Transferencia bancaria',
          labelEn: 'Bank transfer',
          descriptionEs: 'Transferencia desde cuenta bancaria',
          descriptionEn: 'Transfer from bank account',
          icon: <CreditCard className="h-5 w-5" />
        },
        {
          id: 'check',
          value: 'check',
          labelEs: 'Cheque',
          labelEn: 'Check',
          descriptionEs: 'Pago mediante cheque',
          descriptionEn: 'Payment by check',
          icon: <CreditCard className="h-5 w-5" />
        }
      ],
      
      member_status: [
        {
          id: 'active',
          value: 'active',
          labelEs: 'Activo',
          labelEn: 'Active',
          descriptionEs: 'Miembro activo con participación regular',
          descriptionEn: 'Active member with regular participation',
          icon: <Users className="h-5 w-5" />,
          badgeEs: 'Activo',
          badgeEn: 'Active',
          recommended: true
        },
        {
          id: 'inactive',
          value: 'inactive',
          labelEs: 'Inactivo',
          labelEn: 'Inactive',
          descriptionEs: 'Miembro temporalmente inactivo',
          descriptionEn: 'Temporarily inactive member',
          icon: <Users className="h-5 w-5" />,
          badgeEs: 'Inactivo',
          badgeEn: 'Inactive'
        },
        {
          id: 'visitor',
          value: 'visitor',
          labelEs: 'Visitante',
          labelEn: 'Visitor',
          descriptionEs: 'Persona visitante interesada',
          descriptionEn: 'Interested visitor',
          icon: <Users className="h-5 w-5" />,
          badgeEs: 'Visitante',
          badgeEn: 'Visitor'
        }
      ],
      
      event_types: [
        {
          id: 'worship',
          value: 'worship',
          labelEs: 'Culto',
          labelEn: 'Worship service',
          descriptionEs: 'Servicio de adoración dominical',
          descriptionEn: 'Sunday worship service',
          icon: <Calendar className="h-5 w-5" />
        },
        {
          id: 'conference',
          value: 'conference',
          labelEs: 'Conferencia',
          labelEn: 'Conference',
          descriptionEs: 'Evento de conferencias especiales',
          descriptionEn: 'Special conference event',
          icon: <Calendar className="h-5 w-5" />
        },
        {
          id: 'youth',
          value: 'youth',
          labelEs: 'Juvenil',
          labelEn: 'Youth event',
          descriptionEs: 'Evento para jóvenes',
          descriptionEn: 'Event for youth',
          icon: <Users className="h-5 w-5" />
        },
        {
          id: 'children',
          value: 'children',
          labelEs: 'Niños',
          labelEn: 'Children event',
          descriptionEs: 'Evento para niños',
          descriptionEn: 'Event for children',
          icon: <Users className="h-5 w-5" />
        }
      ],
      
      donation_types: [
        {
          id: 'tithe',
          value: 'tithe',
          labelEs: 'Diezmo',
          labelEn: 'Tithe',
          descriptionEs: 'Diezmo regular del 10%',
          descriptionEn: 'Regular 10% tithe',
          icon: <DollarSign className="h-5 w-5" />,
          recommended: true
        },
        {
          id: 'offering',
          value: 'offering',
          labelEs: 'Ofrenda',
          labelEn: 'Offering',
          descriptionEs: 'Ofrenda voluntaria adicional',
          descriptionEn: 'Additional voluntary offering',
          icon: <DollarSign className="h-5 w-5" />
        },
        {
          id: 'special',
          value: 'special',
          labelEs: 'Donación especial',
          labelEn: 'Special donation',
          descriptionEs: 'Donación para proyecto específico',
          descriptionEn: 'Donation for specific project',
          icon: <DollarSign className="h-5 w-5" />
        }
      ]
    }
    
    return safeAccess(optionConfigs, presetType, [])
  }, [presetType])
  
  // Limit options for cognitive load reduction
  const processedOptions = useMemo(() => {
    const allOptions = options.length > 0 ? options : getDefaultOptions()
    return allOptions.slice(0, maxOptions)
  }, [options, getDefaultOptions, maxOptions])
  
  // Validation function
  const validateSelection = useCallback((selectedValue: string): string => {
    if (required && !selectedValue) {
      return locale === 'es'
        ? 'Debe seleccionar una opción'
        : 'Must select an option'
    }
    
    const validOption = processedOptions.find(opt => opt.value === selectedValue)
    if (selectedValue && !validOption) {
      return locale === 'es'
        ? 'Opción no válida'
        : 'Invalid option'
    }
    
    return ''
  }, [required, processedOptions, locale])
  
  // Handle option change
  const handleOptionChange = useCallback((optionValue: string) => {
    if (disabled) return
    
    setInternalValue(optionValue)
    
    // Validate
    const errorMessage = validateSelection(optionValue)
    setValidationError(errorMessage)
    
    // Call parent onChange
    onChange?.(optionValue)
  }, [disabled, validateSelection, onChange])
  
  // Sync external value changes
  useEffect(() => {
    if (value !== undefined && value !== internalValue) {
      setInternalValue(value)
    }
  }, [value, internalValue])
  
  // Style classes
  const sizeClasses = {
    sm: {
      radio: 'h-4 w-4',
      text: 'text-sm',
      description: 'text-xs',
      badge: 'px-2 py-1 text-xs',
      card: 'p-3'
    },
    md: {
      radio: 'h-5 w-5',
      text: 'text-base',
      description: 'text-sm',
      badge: 'px-3 py-1 text-sm',
      card: 'p-4'
    },
    lg: {
      radio: 'h-6 w-6',
      text: 'text-lg',
      description: 'text-base',
      badge: 'px-4 py-2 text-base',
      card: 'p-5'
    }
  }
  
  const variantClasses = {
    default: {
      container: 'space-y-3',
      option: 'flex items-start space-x-3',
      radioContainer: 'relative'
    },
    cards: {
      container: 'space-y-2',
      option: 'border border-gray-200 rounded-lg hover:border-gray-300 transition-colors',
      radioContainer: 'relative'
    },
    minimal: {
      container: 'space-y-2',
      option: 'flex items-start space-x-3',
      radioContainer: 'relative'
    },
    buttons: {
      container: 'grid gap-2',
      option: 'relative',
      radioContainer: 'w-full'
    }
  }
  
  const layoutClasses = {
    vertical: 'space-y-3',
    horizontal: 'flex flex-wrap gap-4',
    grid: 'grid grid-cols-1 sm:grid-cols-2 gap-3'
  }
  
  const currentSizeClasses = safeAccess(sizeClasses, size, sizeClasses.md)
  const currentVariantClasses = safeAccess(variantClasses, variant, variantClasses.default)
  
  // Render individual radio option
  const renderRadioOption = useCallback((option: RadioOption) => {
    const isSelected = internalValue === option.value
    const isDisabled = disabled || option.disabled
    
    const optionLabel = getDisplayText(
      undefined,
      option.labelEs,
      option.labelEn,
      option.value
    )
    
    const optionDescription = getDisplayText(
      option.description,
      option.descriptionEs,
      option.descriptionEn,
      ''
    )
    
    const optionBadge = getDisplayText(
      option.badge,
      option.badgeEs,
      option.badgeEn,
      ''
    )
    
    if (variant === 'buttons') {
      return (
        <div key={option.id} className="relative">
          <input
            type="radio"
            id={option.id}
            name={name}
            value={option.value}
            checked={isSelected}
            onChange={() => handleOptionChange(option.value)}
            disabled={isDisabled}
            className="sr-only"
          />
          <label
            htmlFor={option.id}
            className={cn(
              'block w-full border-2 rounded-lg transition-all duration-200 cursor-pointer',
              currentSizeClasses.card,
              isSelected
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50',
              isDisabled && 'opacity-50 cursor-not-allowed',
              option.recommended && highlightRecommended && !isSelected && 'border-yellow-300 bg-yellow-50'
            )}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                {showIcons && option.icon && (
                  <div className={cn(
                    'mt-1',
                    isSelected ? 'text-primary-600' : 'text-gray-400'
                  )}>
                    {option.icon}
                  </div>
                )}
                
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className={cn(
                      'font-medium',
                      currentSizeClasses.text,
                      isSelected ? 'text-primary-900' : 'text-gray-900'
                    )}>
                      {optionLabel}
                    </span>
                    
                    {option.recommended && highlightRecommended && (
                      <span className={cn(
                        'rounded-full bg-yellow-100 text-yellow-800 font-medium',
                        currentSizeClasses.badge
                      )}>
                        {locale === 'es' ? 'Recomendado' : 'Recommended'}
                      </span>
                    )}
                    
                    {showBadges && optionBadge && (
                      <span className={cn(
                        'rounded-full bg-gray-100 text-gray-700',
                        currentSizeClasses.badge
                      )}>
                        {optionBadge}
                      </span>
                    )}
                  </div>
                  
                  {showDescriptions && optionDescription && (
                    <p className={cn(
                      'mt-1 text-gray-600',
                      currentSizeClasses.description
                    )}>
                      {optionDescription}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Selection indicator */}
              <div className={cn(
                'flex-shrink-0 rounded-full border-2 transition-all duration-200',
                currentSizeClasses.radio,
                isSelected
                  ? 'border-primary-500 bg-primary-500'
                  : 'border-gray-300 bg-white'
              )}>
                {isSelected && (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full" />
                  </div>
                )}
              </div>
            </div>
          </label>
        </div>
      )
    }
    
    // Default, cards, and minimal variants
    const baseClasses = variant === 'cards' 
      ? cn(currentVariantClasses.option, currentSizeClasses.card)
      : currentVariantClasses.option
    
    return (
      <div
        key={option.id}
        className={cn(
          baseClasses,
          isDisabled && 'opacity-50 cursor-not-allowed',
          variant === 'cards' && isSelected && 'border-primary-200 bg-primary-50',
          variant === 'cards' && option.recommended && highlightRecommended && !isSelected && 'border-yellow-200 bg-yellow-50'
        )}
      >
        <div className={currentVariantClasses.radioContainer}>
          <input
            type="radio"
            id={option.id}
            name={name}
            value={option.value}
            checked={isSelected}
            onChange={() => handleOptionChange(option.value)}
            disabled={isDisabled}
            className="sr-only"
          />
          <div
            className={cn(
              'border-2 rounded-full transition-colors duration-200 flex items-center justify-center cursor-pointer',
              currentSizeClasses.radio,
              isSelected
                ? 'border-primary-500 bg-primary-500'
                : 'border-gray-300 bg-white',
              !isDisabled && 'hover:border-primary-400'
            )}
            onClick={() => !isDisabled && handleOptionChange(option.value)}
          >
            {isSelected && (
              <div className="w-2 h-2 bg-white rounded-full" />
            )}
          </div>
        </div>
        
        <div className="flex-1">
          <label
            htmlFor={option.id}
            className={cn(
              'cursor-pointer',
              isDisabled && 'cursor-not-allowed'
            )}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-2">
                {showIcons && option.icon && (
                  <span className={cn(
                    'mt-1',
                    isSelected ? 'text-primary-600' : 'text-gray-400'
                  )}>
                    {option.icon}
                  </span>
                )}
                
                <div>
                  <div className="flex items-center space-x-2">
                    <span className={cn(
                      'font-medium',
                      currentSizeClasses.text,
                      isDisabled ? 'text-gray-400' : 'text-gray-900'
                    )}>
                      {optionLabel}
                    </span>
                    
                    {option.recommended && highlightRecommended && (
                      <span className={cn(
                        'rounded-full bg-yellow-100 text-yellow-800 font-medium',
                        currentSizeClasses.badge
                      )}>
                        {locale === 'es' ? 'Recomendado' : 'Recommended'}
                      </span>
                    )}
                  </div>
                  
                  {showDescriptions && optionDescription && (
                    <p className={cn(
                      'mt-1 text-gray-600',
                      currentSizeClasses.description,
                      isDisabled && 'text-gray-400'
                    )}>
                      {optionDescription}
                    </p>
                  )}
                </div>
              </div>
              
              {showBadges && optionBadge && (
                <span className={cn(
                  'flex-shrink-0 rounded-full bg-gray-100 text-gray-700',
                  currentSizeClasses.badge
                )}>
                  {optionBadge}
                </span>
              )}
            </div>
          </label>
        </div>
      </div>
    )
  }, [
    internalValue,
    disabled,
    variant,
    name,
    handleOptionChange,
    getDisplayText,
    currentSizeClasses,
    currentVariantClasses,
    showIcons,
    showDescriptions,
    showBadges,
    highlightRecommended,
    locale
  ])
  
  return (
    <div ref={ref} className="birhaus-radio-group">
      {/* Label */}
      {displayLabel && (
        <div className="mb-3">
          <h3 className={cn('font-semibold', currentSizeClasses.text)}>
            {displayLabel}
            {required && <span className="text-red-500 ml-1">*</span>}
          </h3>
          
          {displayDescription && (
            <p className={cn('text-gray-600 mt-1', currentSizeClasses.description)}>
              {displayDescription}
            </p>
          )}
        </div>
      )}
      
      {/* Options */}
      <div className={cn(
        currentVariantClasses.container,
        layout !== 'vertical' && safeAccess(layoutClasses, layout, layoutClasses.vertical),
        variant === 'buttons' && layout === 'grid' && 'grid-cols-1 sm:grid-cols-2'
      )}>
        {processedOptions.map(renderRadioOption)}
      </div>
      
      {/* Selection Info */}
      {internalValue && (
        <div className={cn('mt-3 text-gray-600', currentSizeClasses.description)}>
          {locale === 'es' ? 'Seleccionado' : 'Selected'}: {
            processedOptions.find(opt => opt.value === internalValue)?.labelEs ||
            processedOptions.find(opt => opt.value === internalValue)?.labelEn ||
            internalValue
          }
        </div>
      )}
      
      {/* Error Display */}
      {displayError && (
        <div className={cn('mt-2 text-red-600', currentSizeClasses.description)}>
          {displayError}
        </div>
      )}
    </div>
  )
})

BirhausRadioGroup.displayName = 'BirhausRadioGroup'

// Hook for enhanced radio group functionality
export const useBirhausRadioGroup = (options: RadioOption[], initialValue?: string) => {
  const [selectedValue, setSelectedValue] = useState<string>(initialValue || '')
  
  const selectOption = useCallback((value: string) => {
    setSelectedValue(value)
  }, [])
  
  const clearSelection = useCallback(() => {
    setSelectedValue('')
  }, [])
  
  const getSelectedOption = useCallback(() => {
    return options.find(opt => opt.value === selectedValue)
  }, [options, selectedValue])
  
  const isSelected = useCallback((value: string) => {
    return selectedValue === value
  }, [selectedValue])
  
  const getNextOption = useCallback(() => {
    const currentIndex = options.findIndex(opt => opt.value === selectedValue)
    if (currentIndex === -1) return options[0]
    return options[(currentIndex + 1) % options.length]
  }, [options, selectedValue])
  
  const getPreviousOption = useCallback(() => {
    const currentIndex = options.findIndex(opt => opt.value === selectedValue)
    if (currentIndex === -1) return options[options.length - 1]
    return options[currentIndex === 0 ? options.length - 1 : currentIndex - 1]
  }, [options, selectedValue])
  
  return {
    selectedValue,
    setSelectedValue: selectOption,
    clearSelection,
    getSelectedOption,
    isSelected,
    getNextOption,
    getPreviousOption,
    hasSelection: !!selectedValue
  }
}

// IPU Admin specific radio group configurations
export const ipuAdminRadioGroupPresets = {
  paymentMethods: {
    variant: 'cards' as const,
    showIcons: true,
    showDescriptions: true,
    highlightRecommended: true,
    presetType: 'payment_methods' as const
  },
  
  memberStatus: {
    variant: 'buttons' as const,
    layout: 'horizontal' as const,
    showBadges: true,
    highlightRecommended: true,
    presetType: 'member_status' as const
  },
  
  eventTypes: {
    variant: 'default' as const,
    showIcons: true,
    showDescriptions: true,
    presetType: 'event_types' as const
  },
  
  donationTypes: {
    variant: 'cards' as const,
    showIcons: true,
    showDescriptions: true,
    highlightRecommended: true,
    presetType: 'donation_types' as const
  }
}